/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { KEY_ENTER } from '@gh/core-ui';
import { stopEvent } from '@gh/core-util';

import { noop } from 'lodash';

@Component({
  standalone: false,
  selector: 'gh-text-list-filter',
  templateUrl: 'text-list-filter.component.html',
  styleUrls: ['text-list-filter.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: TextListFilterComponent, multi: true }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextListFilterComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  textControl = new FormControl('');
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  @Input() isTriggerOnChange: boolean = false;
  @Input() triggerDebounceMs = 0;
  @Input() placeholder: string = 'lbl_search';
  @Input() focusOnInit: boolean;
  @Input() hideLoop: boolean;

  @Output() trigger = new EventEmitter<string>();

  @Input()
  set value(text: string) {
    this.textControl.setValue(text);
  }

  private _onChange = noop;
  private triggerTimer: any;

  constructor(private changeDetectorRef: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    if (this.focusOnInit) {
      setTimeout(() => { this.searchInput?.nativeElement?.focus(); });
    }
  }

  onSearch(): void {
    this.search();
  }

  onClear(): void {
    this._onChange(void 0);
    this.cancelTriggerDebounce();
    this.search();
  }

  ngOnDestroy(): void {
    this.cancelTriggerDebounce();
  }

  @HostListener('keydown', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    if (event.keyCode === KEY_ENTER) {
      stopEvent(event);

      this.onSearch();
    }
  }

  onInput(value: string): void {
    value = value.trim();
    this._onChange(value.length === 0 ? void 0 : value);
    if (this.isTriggerOnChange) {
      this.scheduleTriggerEmit();
    }
  }

  writeValue(obj: any): void {
    this.textControl.setValue(obj);
    this.changeDetectorRef.markForCheck();
  }

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    // nothing to do
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled === this.textControl.disabled) {
      return;
    }

    if (isDisabled) {
      this.textControl.disable();
    } else {
      this.textControl.enable();
    }
    this.changeDetectorRef.markForCheck();
  }

  private search(): void {
    this.trigger.emit(this.textControl.value ?? undefined);
  }

  private scheduleTriggerEmit(): void {
    if (this.triggerDebounceMs <= 0) {
      this.search();
      return;
    }
    this.cancelTriggerDebounce();
    this.triggerTimer = setTimeout(() => {
      this.triggerTimer = null;
      this.search();
    }, this.triggerDebounceMs);
  }

  private cancelTriggerDebounce(): void {
    if (this.triggerTimer) {
      clearTimeout(this.triggerTimer);
      this.triggerTimer = null;
    }
  }
}
